import introJs from 'intro.js'
import { jsPDF } from 'jspdf'
import { useEffect, useRef, useState } from 'react'

const secondYearEngagedPercent = 100
const overTwoYearsEngagedPercent = 0

export const AUTODIAGSECTIONS = [
  'Identification',
  'Plan de masse',
  'Liste des aliments utilisables',
  'Presence OGM',
  'Services externes',
  'Traçabilité',
  'Synthèse VLOG',
  'Pâturage',
]

export const CARBONENDSTEPLIST = ['DEROGATAIRE', 'PARCOURS TERMINE']

export const MONTHS = [
  'janvier',
  'février',
  'mars',
  'avril',
  'mai',
  'juin',
  'juillet',
  'août',
  'septembre',
  'octobre',
  'novembre',
  'décembre',
]

export const PREMIUM_TABLE_TOTAL_ROW = ['total']

export const OFFCROPSURFACEKEYS = [
  'declared_cap_gaec8',
  'not_declared_cap_invoice',
  'not_declared_cap_farm',
]

export const MAINFORAGESURFACEKEYS = [
  'silage_grain_wet_maize',
  'other_forage_area',
]

export const FIELDS_FOR_PTF_LEVER_1ST_GEN =
  OFFCROPSURFACEKEYS +
  MAINFORAGESURFACEKEYS +
  [
    'temporary_meadow',
    'permanent_grassland',
    'multispecies_mix_legumes_forage',
    'mixture_of_pulses',
    'multispecies_mix_with_pulses',
    'pure_grass',
    'forage_seed_clover',
    'forage_seed_alfalfa',
    'forage_legumes_clover',
    'forage_legumes_alfalfa',
    'mixture_of_leguminous_seeds',
    'uses_farm_saved_seeds',
    'other_crops',
  ]

export const DH_ALL_MY_HIDDEN_SURFACES = [
  'ptf_lever_dh_intercropped_as_main_crop',
  'ptf_lever_dh_intercropped_as_intercropped',
  'ptf_lever_dh_unreported_crops_by_seed_invoice',
  'ptf_lever_dh_crops_as_farm_saved_seed',
]

export const DH_OTHER_GRASSED_AREAS = [
  'ptf_lever_dh_temporary_meadow',
  'ptf_lever_dh_permanent_grassland',
  'ptf_lever_dh_other_grassland',
  'ptf_lever_dh_multispecies_mix_legumes_forage',
  'ptf_lever_dh_mixture_of_pulses',
  'ptf_lever_dh_multispecies_mix_with_legumes',
  'ptf_lever_dh_pure_grass',
  'ptf_lever_dh_forage_legumes_clover',
  'ptf_lever_dh_forage_legumes_alfalfa',
  'ptf_lever_dh_mixture_of_leguminous_seeds',
]

export const BC_ALL_MY_HIDDEN_SURFACES = [
  'ptf_lever_bc_intercropped_as_main_crop',
  'ptf_lever_bc_intercropped_as_intercropped',
  'ptf_lever_bc_unreported_crops_by_seed_invoice',
  'ptf_lever_bc_crops_as_farm_saved_seed',
]

export const BC_OTHER_GRASSED_AREAS = [
  'ptf_lever_bc_temporary_meadow',
  'ptf_lever_bc_permanent_grassland',
  'ptf_lever_bc_other_grassland',
  'ptf_lever_bc_multispecies_mix_legumes_forage',
  'ptf_lever_bc_mixture_of_pulses',
  'ptf_lever_bc_multispecies_mix_with_legumes',
  'ptf_lever_bc_pure_grass',
  'ptf_lever_bc_forage_legumes_clover',
  'ptf_lever_bc_forage_legumes_alfalfa',
  'ptf_lever_bc_mixture_of_leguminous_seeds',
]

export function handleWindowResize(setDevice) {
  let device
  const resolution = window.innerWidth

  if (resolution < 768) {
    device = 'mobile'
  } else if (resolution >= 768 && resolution <= 1024) {
    device = 'tablet'
  } else {
    device = 'desktop'
  }

  setDevice(device)
}

export const useVisible = (initialIsVisible) => {
  const [isVisible, setIsVisible] = useState(initialIsVisible)
  const ref = useRef(null)

  const handleClickOutside = (event) => {
    if (ref.current && !ref.current.contains(event.target)) {
      setIsVisible(false)
    }
  }

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true)
    return () => {
      document.removeEventListener('click', handleClickOutside, true)
    }
  }, [handleClickOutside])

  return { ref, isVisible, setIsVisible }
}

export const getCookie = (name) => {
  // Split cookie string and get all individual name=value pairs in an array
  const cookieArr = document.cookie.split(';')

  // Loop through the array elements
  for (let i = 0; i < cookieArr.length; i += 1) {
    const cookiePair = cookieArr[i].split('=')

    /* Removing whitespace at the beginning of the cookie name
        and compare it with the given string */
    if (name === cookiePair[0].trim()) {
      // Decode the cookie value and return
      return decodeURIComponent(cookiePair[1])
    }
  }

  // Return null if not found
  return null
}

export function startTour() {
  introJs()
    .setOptions({
      doneLabel: 'Terminer',
      nextLabel: 'Suivant',
      overlayOpacity: 0.75,
      prevLabel: 'Précédent',
      skipLabel: '',
    })
    .start()
}

export const formatErrors = (error) => {
  let messages = []
  const formattedErrors = {}
  const errorsObject = error.response.data
  switch (error.response.status) {
    case 413:
      messages = ['Le fichier est trop volumineux.']
      break
    default:
      // eslint-disable-next-line no-restricted-syntax
      for (const property in errorsObject) {
        if (property === 'non_field_errors') {
          messages.push(...errorsObject[property])
        } else {
          messages.push(errorsObject[property])
          formattedErrors[property] = { message: errorsObject[property][0] }
        }
      }
  }
  return { formattedErrors, messages }
}

export const formatDate = (date) => {
  if (!date) return ''
  const newDate = new Date(date)
  return newDate.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

export const formatDateTime = (date) => {
  if (!date) return ''
  const newDate = new Date(date)
  return newDate.toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  })
}

export const addYears = (date, years) => {
  const newDate = new Date(date)
  newDate.setFullYear(newDate.getFullYear() + years)
  return newDate
}

export const addDays = (date, days) => {
  const newDate = new Date(date)
  newDate.setDate(newDate.getDate() + days)
  return newDate
}

export const capitalize = (word) => word.charAt(0).toUpperCase() + word.slice(1)

// Rasterize an inline SVG into a PNG <img>, as jsPDF cannot draw SVG images.
// Returns false if the SVG could not be rasterized.
const replaceSvgWithPng = (svg) =>
  new Promise((resolve) => {
    const { width, height } = svg.getBoundingClientRect()
    if (!width || !height) {
      resolve(false)
      return
    }
    const ownerDocument = svg.ownerDocument
    const { color } = ownerDocument.defaultView.getComputedStyle(svg)
    const svgCopy = svg.cloneNode(true)
    svgCopy.setAttribute('xmlns', 'http://www.w3.org/2000/svg')
    svgCopy.setAttribute('width', width)
    svgCopy.setAttribute('height', height)
    const markup = new XMLSerializer()
      .serializeToString(svgCopy)
      .replaceAll('currentColor', color)

    const image = new Image()
    image.onload = () => {
      const scale = 4 // keep the icon sharp once scaled into the PDF
      const canvas = ownerDocument.createElement('canvas')
      canvas.width = width * scale
      canvas.height = height * scale
      canvas
        .getContext('2d')
        .drawImage(image, 0, 0, canvas.width, canvas.height)
      const png = ownerDocument.createElement('img')
      png.width = width
      png.height = height
      png.className = svg.getAttribute('class') || ''
      png.style.cssText = svg.style.cssText
      // Wait for the PNG to be decoded: html2canvas skips images without
      // natural size (Firefox decodes data URLs asynchronously)
      png.onload = () => {
        svg.replaceWith(png)
        resolve(true)
      }
      png.onerror = () => resolve(false)
      png.src = canvas.toDataURL('image/png')
    }
    image.onerror = () => resolve(false)
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(markup)}`
  })

export const exportToPDF = (title, body, fileName) => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF('portrait', 'mm', 'a4')
  doc.html(body, {
    callback(document) {
      document.text(title, 10, 15, {
        maxWidth: 190,
      })
      document.save(`${fileName}.pdf`)
    },
    x: 10,
    y: 40,
    width: 190,
    windowWidth: 595,
    html2canvas: {
      // Do not render interactive map controls (zoom buttons, etc.)
      ignoreElements: (element) => element.classList?.contains('mapboxgl-ctrl'),
      // jsPDF cannot draw SVG images: a failed draw leaves a clipping path
      // open, which hides everything rendered afterwards (e.g. the map).
      // SVGs are converted to PNG, or hidden if the conversion fails.
      onclone: async (clonedDocument) => {
        const svgs = [...clonedDocument.querySelectorAll('svg')]
        const results = await Promise.all(svgs.map(replaceSvgWithPng))
        svgs.forEach((svg, index) => {
          if (!results[index]) {
            svg.style.visibility = 'hidden'
          }
        })
        // Fit maps to the export width: scale the canvas container (map and
        // markers together) so that markers stay at the right place
        for (const map of clonedDocument.querySelectorAll('.mapboxgl-map')) {
          const container = map.querySelector('.mapboxgl-canvas-container')
          const canvas = map.querySelector('.mapboxgl-canvas')
          const mapWidth = map.getBoundingClientRect().width
          const canvasWidth = parseFloat(canvas?.style.width)
          const canvasHeight = parseFloat(canvas?.style.height)
          if (container && mapWidth && canvasWidth && canvasHeight) {
            const ratio = mapWidth / canvasWidth
            container.style.transformOrigin = 'top left'
            container.style.transform = `scale(${ratio})`
            map.style.height = `${canvasHeight * ratio}px`
          }
        }
      },
    },
  })
}

export const handleAllCheck = (event, list) => {
  event.stopPropagation()

  if (event.target.checked) {
    const newSelectedIds = []
    list.data.pages.forEach((page) => {
      page.results.forEach((holding) => newSelectedIds.push(holding.id))
    })
    return newSelectedIds
  }
  return []
}

export const handleCheck = (id, selectedIds) => {
  if (selectedIds.includes(id)) {
    return [...selectedIds.filter((selectedId) => selectedId !== id)]
  }
  return [...selectedIds, id]
}

export const formatToDjangoDateFormat = (date) => {
  let newDate = date.getFullYear()
  const month = date.getMonth() + 1
  const day = date.getDate()

  if (month <= 9) {
    newDate += `-0${month}`
  } else {
    newDate += `-${month}`
  }
  if (day <= 9) {
    newDate += `-0${day}`
  } else {
    newDate += `-${day}`
  }
  return newDate
}

/**
 * getDays is a function that takes in three parameters:
 * 
 * @param {Date} date - The date to use as a starting point.
 * @param {Number} offset - The number of days offset from the date.
 * @param {String} target - The target date: 'month' or 'week'.
 * 
 * @returns {Array} An array of objects containing information about the days in the specified date range, including the day's name, number, formatted date, month number, and year.
 *
 * The function first sets up some variables: an array of names for the days of the week, a constant for the number of milliseconds in a day, and an empty array where the day information will be stored. The length variable is set depending on whether the target is a month or a week. The offset is set to a positive number if it is negative.

 * The start day is set to the first day of the month or the first day of the week, depending on the target. The loop then iterates over the length, adding an object containing the day's date, day label, day number, formatted date, month number, and year to the days array. The function then returns the days array.
 */
export const getDays = (date, offset, target) => {
  const name = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim']
  const DAY = 60 * 60 * 24 * 1000
  const days = []
  const length =
    target === 'month'
      ? new Date(
          date.getFullYear(),
          date.getMonth() + offset + 1,
          0,
          2,
        ).getDate()
      : 7
  const newOffset = offset > 0 ? offset : offset * 7
  date.setDate(date.getDate() + newOffset)
  let startDay = new Date(date.getFullYear(), date.getMonth(), 1, 2)
  if (target === 'week') {
    const simple = new Date(
      date.getFullYear(),
      0,
      1 + (date.getWeekNumber() - 1) * 7,
    )
    const dow = simple.getDay()
    startDay = simple

    if (dow <= 4) startDay.setDate(simple.getDate() - simple.getDay() + 1)
    else startDay.setDate(simple.getDate() + 8 - simple.getDay())
  }
  for (let i = 0; i < length; i += 1) {
    const day = new Date(Date.parse(startDay) + DAY * i)
    days.push({
      date: day,
      dayLabel: name[i],
      dayNumber: day.getDate(),
      formattedDate: formatToDjangoDateFormat(day),
      monthNumber: day.getMonth() + 1,
      year: day.getFullYear(),
    })
  }
  return days
}

export const getAllGrazedDays = (grazingLogs) => {
  let allGrazedDays = []
  grazingLogs.forEach((parcel) => {
    allGrazedDays = [...new Set([...allGrazedDays, ...parcel.planning])]
  })
  return allGrazedDays
}

export const getMonthlyGrazedDays = (planningData, month) => {
  let currentMonthGrazedDays = 0
  getAllGrazedDays(planningData.grazing_logs).forEach((day) => {
    const parcelMonth = new Date(day).getMonth()
    if (month === parcelMonth) currentMonthGrazedDays += 1
  })
  return `${currentMonthGrazedDays} jour${
    currentMonthGrazedDays > 1 ? 's' : ''
  }`
}

// eslint-disable-next-line no-extend-native
Date.prototype.getWeekNumber = function getWeekNumber() {
  const d = new Date(
    Date.UTC(this.getFullYear(), this.getMonth(), this.getDate()),
  )
  const dayNum = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1))
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7)
}

export const isAdmin = () => localStorage.getItem('role') === 'administrator'

export const isAuditor = () => localStorage.getItem('role') === 'auditor'

export const isBreeder = () => localStorage.getItem('role') === 'breeder'

export const isDevelopment = () => process.env.REACT_APP_ENV === 'development'

export const isStaging = () => process.env.REACT_APP_ENV === 'staging'

export const Switch = ({ test, defaultComponent, children }) =>
  children.find((child) => child.props.value === test) || defaultComponent

export const Case = ({ children }) => children

export const setIframeImgSize = (index) => {
  document
    .querySelector(`#iframe-${index}`)
    .contentWindow.document.querySelector('img').style.margin = 'auto'
  document
    .querySelector(`#iframe-${index}`)
    .contentWindow.document.querySelector('img').style.maxWidth = '100%'
  document.querySelector(`#iframe-${index}`).height = document.querySelector(
    `#iframe-${index}`,
  ).contentWindow.document.body.scrollHeight
}

export const getReferenceLabel = (referenceKey, references, source = '') => {
  let label = ''
  for (let index = 0; index < references.length; index += 1) {
    if (source === 'id' && references[index].id === referenceKey) {
      label = references[index].name
    }
    if (source !== 'id' && Object.hasOwn(references[index], referenceKey)) {
      label = references[index][referenceKey]
    }
  }
  return label
}

export const gapBetween2Dates = (date1, date2) => {
  const difference = date1.getTime() - date2.getTime()
  const totalDays = Math.ceil(difference / (1000 * 3600 * 24))
  const weeks = Math.round(totalDays / 7)
  const months = Math.round(totalDays / 31)
  const years = Math.round(totalDays / 365)
  if (totalDays >= 365) {
    return `Il y a ${years} an${years > 1 ? 's' : ''}`
  }
  if (totalDays >= 31) {
    return `Il y a ${months} mois`
  }
  if (totalDays >= 7) {
    return `Il y a ${weeks} semaine${weeks > 1 ? 's' : ''}`
  }
  return `il y a ${totalDays} jour${totalDays > 1 ? 's' : ''}`
}

export const getDocumentIdFromURL = (url) => url.split('/').at(-2)

export const formattedDate = (dateStr, shortBool) => {
  let short = true
  if (typeof shortBool === 'undefined') short = false
  const options = { month: 'short', day: 'numeric' }
  if (short) {
    return new Date(dateStr).toLocaleDateString('fr-FR', options)
  }
  const combinedOptions = { ...options, ...{ year: 'numeric' } }
  return new Date(dateStr).toLocaleDateString('fr-FR', combinedOptions)
}

export const dateDiff = (secondDateStr, firstDateStr) => {
  /* Calculate the time difference in milliseconds between the two dates.
    If the 1st date is not indicated, the current date is used instead.
    Divide the time difference by the number of milliseconds in a day (1000 * 60 * 60 * 24). */
  let firstDate = new Date(firstDateStr)
  if (typeof firstDateStr === 'undefined') firstDate = new Date()
  const secondDate = new Date(secondDateStr)
  const differenceInMilliseconds = secondDate.valueOf() - firstDate.valueOf()
  const divisor = 1000 * 60 * 60 * 24
  return Math.round(differenceInMilliseconds / divisor)
}

export const fnIsCarbonProgramOpened = (startDateStr, endDateStr) => {
  const start = new Date(startDateStr)
  start.setHours(1, 0, 0)
  const end = new Date(endDateStr)
  end.setHours(23, 59, 0)
  const today = Date.now() // same as getTime
  return today >= start.valueOf() && today <= end.valueOf()
}

export const getRemainingDays = (intRemainingDay, closing_date) => {
  let startStr = 'reste 1 jour'
  if (intRemainingDay > 0) startStr = `reste ${1 + intRemainingDay} jours`
  else if (intRemainingDay < 0) startStr = 'dernier jour'
  return `${startStr} (${closing_date})`
}

export const getSmartNextPeriod = (opening_date) => {
  const today = new Date()
  const openingDate = new Date(opening_date)
  if (today > openingDate) {
    return new Date(
      1 + openingDate.getFullYear(),
      openingDate.getMonth(),
      openingDate.getDate(),
    ) // add 1 year
  }
  return opening_date
}

/*
 * In the frontend,
 * points are calculated based on a single ‘increasing case’ direction.
 * All levers have a positive step, except for the MRM lever,
 * so a single-direction calculation is sufficient because
 * the MRM lever is not included in the overall premium calculation.
 * This calculation method also approves the percentage values.
 * Calculate points from a base value and some references of the lever.
 */
export function computePoints(lever, baseValue) {
  let points = 0.0
  let bottomLimit = parseFloat(lever.low_threshold)
  const highThreshold = parseFloat(lever.high_threshold)

  // checks whether the value is within the limit range
  if (baseValue < bottomLimit) {
    // returns 0 if the value is outside the low limit
    return points
  }
  if (baseValue >= highThreshold) {
    // returns value max if the value is outside the high limit
    return parseFloat(lever.theoretical_max_points)
  }

  // increments the low limit until the high limit is reached
  while (bottomLimit <= highThreshold) {
    // checks whether the given value is higher than the current low limit
    if (baseValue >= bottomLimit) {
      points += parseFloat(lever.points_per_step) // adds points for each step taken
    } else {
      break
    }
    // increments the lower limit by the defined step
    bottomLimit += parseFloat(lever.incremental_step)
  }

  return points
}

/* get 30% of value */
function get30Percent(value) {
  return (30.0 * value) / 100.0
}

/* convert a list of fields into a calculated value, a generic base value */
function prepareValueGeneric({ lever, currentData }) {
  return lever.fields.reduce((baseValue, field) => {
    if (field.type === 'DecimalField') {
      const value = parseFloat(currentData?.[field.name])

      return Number.isNaN(value) ? baseValue : baseValue + value
    }

    if (field.type === 'BooleanField' && currentData?.[field.name]) {
      return 100.0
    }

    return baseValue
  }, 0.0)
}

/* get ptf lever base value for 1st generation */
function _prepareValuePtfLever1stGen({ lever, currentData }) {
  let baseValue = 0.0
  let catchCropValue = 0.0
  const notAllowedKeys = OFFCROPSURFACEKEYS.concat(MAINFORAGESURFACEKEYS)

  lever.fields.forEach((field) => {
    if (
      field.type === 'DecimalField' &&
      !notAllowedKeys.includes(field.name) &&
      currentData?.[field.name]
    ) {
      baseValue += parseFloat(currentData[field.name] || 0)
    }
    if (
      field.type === 'DecimalField' &&
      OFFCROPSURFACEKEYS.includes(field.name) &&
      currentData?.[field.name]
    ) {
      catchCropValue += parseFloat(currentData[field.name] || 0)
    }
  })

  const total_grassland_area = baseValue + get30Percent(catchCropValue)
  const main_forage_area =
    total_grassland_area +
    parseFloat(currentData?.silage_grain_wet_maize || 0) +
    parseFloat(currentData?.other_forage_area || 0)
  return (total_grassland_area / main_forage_area) * 100.0
}

/* get ptf lever base value for 2nd generation */
function prepareValuePtfLever({ lever, currentData }) {
  const { baseValue, derivativesValue } = lever.fields.reduce(
    (acc, field) => {
      const value = parseFloat(currentData?.[field.name]) || 0
      if (field.type !== 'DecimalField' || !value) return acc

      if (DH_OTHER_GRASSED_AREAS.includes(field.name)) acc.baseValue += value
      if (DH_ALL_MY_HIDDEN_SURFACES.includes(field.name))
        acc.derivativesValue += value

      return acc
    },
    { baseValue: 0.0, derivativesValue: 0.0 },
  )

  const totalHectaresPasture = baseValue + 0.3 * derivativesValue
  const volume = parseFloat(currentData?.ptf_lever_contract_volume)
  const hectareOfGrassBy1000Liters = volume
    ? (1000 * totalHectaresPasture) / volume
    : 0.0

  // returns the ratio for computePoints AND the gross area for the state
  return {
    ratio: hectareOfGrassBy1000Liters,
    totalHectaresPasture,
  }
}

/* get specific base value for all levers */
const prepareValueActions = {
  gc_lever: prepareValueGeneric,
  zdlf_lever: prepareValueGeneric,
  mph_lever: prepareValueGeneric,
  pc_lever: prepareValueGeneric,
  ptf_lever: prepareValuePtfLever,
  mrf_lever: prepareValueGeneric,
  mrm_lever: prepareValueGeneric,
}

export function getBonus(points, param) {
  return (param?.bonus_calculation * points) / param?.bonus_calculation_divisor
}

const postMrfLeverCalulations = (__, currentData, baseValue) => {
  if (currentData?.is_engaged_over_2_years) {
    return baseValue * (overTwoYearsEngagedPercent / 100)
  } else if (currentData?.is_engaged_second_year) {
    return baseValue * (secondYearEngagedPercent / 100)
  }
  return baseValue
}

/* get post specific calculations points for all levers */
const postCalculations = {
  gc_lever: (__, ___, baseValue) => baseValue,
  zdlf_lever: (__, ___, baseValue) => baseValue,
  mph_lever: (__, ___, baseValue) => baseValue,
  pc_lever: (__, ___, baseValue) => baseValue,
  ptf_lever: (__, ___, baseValue) => baseValue,
  mrf_lever: (lever, currentData, baseValue) =>
    postMrfLeverCalulations(lever, currentData, baseValue),
  mrm_lever: (__, ___, baseValue) => baseValue,
}

export function getPoints(lever, currentData) {
  const result = prepareValueActions[lever.code]({ lever, currentData })

  // handling the specific case ‘ptf_lever’ versus the generic case
  const baseValue = typeof result === 'object' ? result.ratio : result
  const rawData = typeof result === 'object' ? result : { baseValue }

  const points = computePoints(lever, baseValue)
  const postPoints = postCalculations[lever.code](lever, currentData, points)

  return [postPoints, rawData]
}

export const getLeverPercent = (lever, currentData, isHistoryMode) => {
  if (isHistoryMode) {
    return parseFloat(currentData?.[`percent_${lever.code}`]) || 0
  }
  const [points, __] = getPoints(lever, currentData)
  return Math.round((points * 100.0) / parseFloat(lever.theoretical_max_points))
}

export const roundTo = (value, decimalPlaces = 3) => {
  const factor = 10 ** decimalPlaces
  const baseValue = Math.round(value * factor) / factor
  return baseValue.toFixed(decimalPlaces)
}

export const toggleArrayElement = (array, element) => {
  const set = new Set(array)
  set.has(element) ? set.delete(element) : set.add(element)
  return [...set]
}

/* convert a snake case string to a camel case string */
export const snakeToStringCamel = (snakeKey, bonusOrPoints = true) => {
  const parts = snakeKey.split('_')
  const camel = parts
    .map((part, index) =>
      index === 0
        ? part.charAt(0).toUpperCase() + part.slice(1)
        : part.charAt(0).toUpperCase() + part.slice(1),
    )
    .join('')
  const slug = bonusOrPoints ? 'bonus' : 'points'
  return `${slug}${camel}`
}

export const buildDataStructure = (levers) => {
  const structure = {}

  levers?.forEach((lever) => {
    lever.fields.forEach((field) => {
      structure[field.name] = field.type === 'BooleanField' ? false : 0.0
    })
  })

  return structure
}

// normalize soft
export const normalizeSoft = (obj) => {
  if (!obj) return obj

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (value === null || value === undefined) return [key, value]

      // convertir nombres string -> number
      if (!Number.isNaN(value) && value !== '') {
        return [key, Number(value)]
      }

      return [key, value]
    }),
  )
}

export function clearFields(payload, keys) {
  keys.forEach((key) => {
    payload[key] = 0.0
  })
}

// normalize tough
export const normalize = (obj) => {
  if (!obj) return obj

  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => {
      if (value === null || value === undefined) return [key, value]

      // boolean → ok
      if (typeof value === 'boolean') return [key, value]

      // number string → number
      if (
        typeof value === 'string' &&
        value.trim() !== '' &&
        !Number.isNaN(value)
      ) {
        return [key, Number(value)]
      }

      return [key, value]
    }),
  )
}
